// custom_close_report.js
//
// Custom close report: one station, several daily close reports combined into
// ONE period, exported as the FULL close report - the same sheets as the
// daily download (CASH SUMMARY, STOCK REPORT, CREDIT, DELIVERIES, CASH
// DISCOUNT DETAILS, ORDERS), built by the same closeReportExcelHelper.js.
//
// Which daily reports are combined:
//   every close report of the station that STARTS between the chosen start
//   date and the chosen end date (both days included).
//   Example: 20th to 22nd -> the reports starting on the 20th, 21st and 22nd
//   (3 reports). The period runs from the first report's start (20th, e.g.
//   06:04) to the last report's end (normally the morning of the 23rd).
//
// How the period is combined (mergeReports below):
//   CASH SUMMARY
//     OPENING AMOUNT          = the FIRST day's opening cash only
//     FUEL DISPENSED, PUMP TEST, EXPENSES, DISCOUNTS, CREDITS, COLLECTIONS
//                             = all days added together
//     TO BE DEPOSITED         = opening + dispensed - all the deductions
//     DEPOSITED               = every day's deposit added together
//     CASH CLOSING BALANCE    = to be deposited - deposited
//   STOCK REPORT
//     opening meter = first report the nozzle appears in, closing meter =
//     last one, LTRS = every day's LTRS added; opening stock = first day,
//     ATG = last day, received = every delivery added
//   COLLECTIONS
//     ONE line per provider with the period total: "SELCOM PESA", "SELCOM
//     MONEY" and "SELCOM" on different days -> one SELCOM line (see
//     providerKey below)
//   CREDIT / CASH DISCOUNT / EXPENSES
//     every line from every day
//   DELIVERIES
//     every delivery from every day, with its date
//
// Prices: the pump price can change inside the period (it changes monthly),
// so every expense/credit/discount/pump-test line is valued at the price of
// ITS OWN day, and each pump's litres at the period's weighted-average price
// for that product (= the exact money total of the daily reports).
//
// ! A daily report carries at most ONE delivery per tank (the station's
// latest refill that day). This report can only combine what the daily
// reports contain.

import React, { useEffect, useState } from 'react';
import * as XLSX from 'xlsx-js-style';
import Button from 'react-bootstrap/Button';
import TextField from '@material-ui/core/TextField';
import { Autocomplete } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import FormHelperText from '@material-ui/core/FormHelperText';
import FormControl from '@material-ui/core/FormControl';

import { buildCloseReportTemplateWorkbook } from './closeReportExcelHelper';
import { API_BASE } from '../config';
import { tr } from '../i18n';

const useStyles = makeStyles((theme) => ({
  formControl: {
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1),
    minWidth: 180,
  },
}));

// ─── Fetching ─────────────────────────────────────────────────────────────────
// The server filters: this station, start_date >= the first day, end_date <=
// the last day, 100 per page (its maximum). With the station filter in place
// on the backend that is normally ONE request.
//
// The same checks are repeated here on purpose: if the backend has not got
// the station_serial filter yet, it ignores it and returns every station's
// reports in the range - slower, but still correct.
const PER_PAGE = 100;
const CUSTOM_MAX_PAGES = 100;
const dateOnly = (iso) => (iso || '').slice(0, 10);
const minuteOf = (iso) => (iso || '').slice(0, 16).replace('T', ' ');

// The server can only bound start_date from below and end_date from above,
// so the request asks for reports starting on/after the start date and
// ending within END_SLACK_DAYS of the end date - room for the last report
// (which starts on the end date) to close the next morning, or a few days
// later if the station skipped closing. The exact "starts on or before the
// end date" check is done below.
const END_SLACK_DAYS = 31;

function addDays(day, n) {
  const d = new Date(`${day}T00:00:00`);
  d.setDate(d.getDate() + n);
  const pad = (x) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export async function fetchStationReports(token, serial, fromDate, toDate, onPage) {
  const params = new URLSearchParams({
    station_serial: serial,
    start_date: `${fromDate}T00:00:00`,
    end_date: `${addDays(toDate, END_SLACK_DAYS)}T23:59:59`,
    per_page: String(PER_PAGE),
  });

  // keyed by start_date: the same period synced twice is kept once (the
  // latest generated_at wins, like findCloseReport)
  const byStart = {};
  for (let page = 1; page <= CUSTOM_MAX_PAGES; page++) {
    if (onPage) onPage(page);
    params.set('page', String(page));
    const res = await fetch(`${API_BASE}/ak4tek/close-reports?${params.toString()}`, {
      headers: { Authorization: token },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const body = await res.json();
    const reports = body.reports || [];

    reports.forEach((r) => {
      if (r.station_serial !== serial) return;
      const startDay = dateOnly(r.start_date);
      if (startDay < fromDate || startDay > toDate) return;
      const key = minuteOf(r.start_date);
      const prev = byStart[key];
      if (!prev || (r.generated_at || '') > (prev.generated_at || '')) byStart[key] = r;
    });

    if (reports.length === 0 || !body.pagination || !body.pagination.has_next) break;
  }
  return Object.values(byStart).sort((a, b) => (a.start_date || '').localeCompare(b.start_date || ''));
}

// Holes in the chain: each daily report should start where the previous one
// ended. A hole means a missing close report - its sales and deliveries are
// not in this custom report.
export function findGaps(reports) {
  const gaps = [];
  for (let i = 1; i < reports.length; i++) {
    const prevEnd = minuteOf(reports[i - 1].end_date);
    const start = minuteOf(reports[i].start_date);
    if (prevEnd && start && prevEnd !== start) gaps.push({ from: prevEnd, to: start });
  }
  return gaps;
}

// ─── Combining ────────────────────────────────────────────────────────────────
const DIESEL_NAMES   = new Set(['DIESEL', 'AGO', 'GASOIL']);
const UNLEADED_NAMES = new Set(['UNLEADED', 'PETROL', 'PMS', 'GASOLINE']);
const GROUPS = ['PETROL', 'DIESEL'];
// closing_form totals are keyed UNLEADED / DIESEL, prices PETROL / DIESEL
const TOTALS_KEY = { PETROL: 'UNLEADED', DIESEL: 'DIESEL' };
const FORM_SECTIONS = ['pump_test', 'cash_discount', 'cash_expenses', 'collections', 'credit', 'fuel_expenses'];

function productGroup(productName) {
  const p = (productName || '').toUpperCase();
  if (DIESEL_NAMES.has(p))   return 'DIESEL';
  if (UNLEADED_NAMES.has(p)) return 'PETROL';
  return null;
}

const num = (v) => Number(v) || 0;

const hasDelivery = (t) =>
  !!t && !!(t.TankerPlate || t.DriverName || t.SupplierName || num(t.OrderQty) > 0 || num(t.ATGDeliveryVolume) > 0);

// Same price lookup the Excel helper uses for a daily report.
function priceMapOf(rep) {
  const map = {};
  (rep.products_list || []).forEach((p) => {
    const g = productGroup(p.product);
    if (g) map[g] = num(p.Price);
  });
  return map;
}

const formOf = (rep) => (rep.closing_form && Object.keys(rep.closing_form).length ? rep.closing_form : {});

// Collection provider for grouping: the same provider written differently on
// different days is one line. Known names first, otherwise the first word:
//   "SELCOM PESA", "SELCOM MONEY", "Selcom"      -> SELCOM
//   "M PESA", "M-PESA", "MPESA"                  -> M-PESA
//   "CRDB EVENING DEPOSIT", "CRDB ATL TRANSFER"  -> CRDB
//   "ABSA EVENING DEPOSIT", "ABSA"               -> ABSA
const PROVIDER_ALIASES = [
  ['M-PESA',       /^M[\s-]*PESA\b|^VODA/],
  ['MIXX BY YAS',  /^MIXX\b|^TIGO[\s-]*PESA\b|^YAS\b/],
  ['AIRTEL MONEY', /^AIRTEL\b/],
  ['HALOPESA',     /^HALO[\s-]*PESA\b/],
  ['AZAMPESA',     /^AZAM[\s-]*PESA\b/],
];

export function providerKey(name) {
  const s = String(name || '').trim().toUpperCase().replace(/\s+/g, ' ');
  if (!s) return 'OTHER';
  const alias = PROVIDER_ALIASES.find(([, re]) => re.test(s));
  if (alias) return alias[0];
  return s.split(/[\s(\/,.-]/)[0] || s;
}

// Every day's collection lines -> one line per provider, biggest first.
export function groupCollections(rows) {
  const byProvider = {};
  rows.forEach((row) => {
    const key = providerKey(row.provider);
    if (!byProvider[key]) byProvider[key] = { provider: key, reference: '', amount: 0, lines: 0 };
    byProvider[key].amount += num(row.amount);
    byProvider[key].lines += 1;
  });
  return Object.values(byProvider).sort((a, b) => b.amount - a.amount);
}

function addInto(target, source) {
  Object.entries(source || {}).forEach(([k, v]) => { target[k] = num(target[k]) + num(v); });
  return target;
}

// reports must be sorted oldest -> newest (fetchStationReports does that).
// Returns ONE report in the same shape as a daily close report, so the
// normal Excel builder can turn it into the full workbook.
export function mergeReports(reports) {
  const first = reports[0];
  const last  = reports[reports.length - 1];

  // ── Pumps ──
  const pumps = {};
  const groupMoney = { PETROL: 0, DIESEL: 0 };
  const groupLitres = { PETROL: 0, DIESEL: 0 };
  reports.forEach((rep) => {
    const prices = priceMapOf(rep);
    (rep.pumps_list || []).forEach((p) => {
      const name = p.pump || '';
      if (!name) return;
      const g = productGroup(p.product);
      const litres = num(p.total_volume_by_sale);
      if (!pumps[name]) {
        pumps[name] = { pump: name, product: p.product, electronic_totalizer: num(p.electronic_totalizer), total_volume_by_sale: 0 };
      }
      pumps[name].last_electronic_totalizer = num(p.last_electronic_totalizer);
      pumps[name].total_volume_by_sale += litres;
      if (p.product) pumps[name].product = p.product;
      if (g) {
        groupMoney[g] += litres * (prices[g] || num(p.Price));
        groupLitres[g] += litres;
      }
    });
  });
  const lastPrices = priceMapOf(last);
  const avgPrice = (g) => (groupLitres[g] ? groupMoney[g] / groupLitres[g] : (lastPrices[g] || 0));
  const pumps_list = Object.values(pumps)
    .sort((a, b) => a.pump.localeCompare(b.pump, undefined, { numeric: true }))
    .map((p) => ({ ...p, pump_price: avgPrice(productGroup(p.product)) }));

  // ── Tanks + deliveries ──
  const tanks_list = [];
  GROUPS.forEach((group) => {
    const tankOf = (rep) => (rep.tanks_list || []).find((t) => productGroup(t.TankProdName) === group);
    const withTank = reports.filter((rep) => tankOf(rep));
    if (!withTank.length) return;
    const deliveries = [];
    reports.forEach((rep) => {
      const t = tankOf(rep);
      if (!hasDelivery(t)) return;
      deliveries.push({
        time:     t.AutorefillIssueDate || dateOnly(rep.end_date),
        driver:   t.DriverName || '',
        plate:    t.TankerPlate || '',
        qty:      num(t.OrderQty),
        atgQty:   num(t.ATGDeliveryVolume),
        supplier: t.SupplierName || '',
      });
    });
    const firstTank = tankOf(withTank[0]);
    tanks_list.push({
      TankProdName:      firstTank.TankProdName,
      StartVolume:       num(firstTank.StartVolume),
      EndVolume:         num(tankOf(withTank[withTank.length - 1]).EndVolume),
      OrderQty:          deliveries.reduce((s, d) => s + d.qty, 0),
      ATGDeliveryVolume: deliveries.reduce((s, d) => s + d.atgQty, 0),
      deliveries,
    });
  });

  // ── Closing form ──
  const closing_form = {
    // Opening cash of the FIRST day only - the later days' openings are just
    // the previous day's money carried forward, not new money.
    opening_cash: num(formOf(first).opening_cash),
    to_be_deposited: 0,
    totals: {
      pump_test_by_product: {}, cash_discount_by_product: {},
      credit_by_product: {}, fuel_expenses_by_product: {},
      cash_expenses_amount: 0, collections_amount: 0,
      pump_test_amount: 0,
    },
  };
  FORM_SECTIONS.forEach((k) => { closing_form[k] = []; });

  reports.forEach((rep) => {
    const form = formOf(rep);
    const prices = priceMapOf(rep);
    const day = dateOnly(rep.end_date);
    closing_form.to_be_deposited += num(form.to_be_deposited);

    FORM_SECTIONS.forEach((k) => {
      (form[k] || []).forEach((row) => {
        // Each line keeps the price of the day it came from.
        closing_form[k].push({ ...row, day, pump_price: prices[productGroup(row.product)] || 0 });
      });
    });

    const totals = form.totals || {};
    ['pump_test_by_product', 'cash_discount_by_product', 'credit_by_product', 'fuel_expenses_by_product']
      .forEach((k) => addInto(closing_form.totals[k], totals[k]));
    closing_form.totals.cash_expenses_amount += num(totals.cash_expenses_amount);
    closing_form.totals.collections_amount   += num(totals.collections_amount);

    const pumpTest = totals.pump_test_by_product || {};
    GROUPS.forEach((g) => {
      closing_form.totals.pump_test_amount += num(pumpTest[TOTALS_KEY[g]]) * (prices[g] || 0);
    });
  });

  closing_form.collections = groupCollections(closing_form.collections);

  return {
    station_serial: first.station_serial,
    start_date: first.start_date,
    end_date: last.end_date,
    generated_at: last.generated_at,
    products_list: last.products_list || [],
    pumps_list,
    tanks_list,
    closing_form,
  };
}

// ─── React component ─────────────────────────────────────────────────────────
// `stations` is the page's already-scoped station list, so a Manager only
// ever sees their own station here, same as the rest of CloseReports.
export default function CustomCloseReport({ stations = [], accessToken, LogoutTimeout }) {
  const classes = useStyles();
  const [serial,   setSerial]   = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate,   setToDate]   = useState('');
  const [busy,     setBusy]     = useState(false);
  const [progress, setProgress] = useState('');
  const [message,  setMessage]  = useState('');

  // A single-station user is locked to their one station.
  useEffect(() => {
    if (stations.length === 1) setSerial(stations[0].serial_number);
  }, [stations]);

  const station = stations.find((s) => s.serial_number === serial);

  const handleGenerate = async () => {
    setMessage('');
    if (!serial)              { setMessage(tr("Please select a station.")); return; }
    if (!fromDate || !toDate) { setMessage(tr("Please select a start and an end date.")); return; }
    if (fromDate > toDate)    { setMessage(tr("The start date must be before the end date.")); return; }

    setBusy(true);
    try {
      if (LogoutTimeout) LogoutTimeout();

      const reports = await fetchStationReports(accessToken, serial, fromDate, toDate,
        (page) => setProgress(tr("Searching close reports", page)));

      if (reports.length === 0) {
        setMessage(tr("No close reports for {0} between {1} and {2}.", station ? station.name : serial, fromDate, toDate));
        return;
      }

      const merged = mergeReports(reports);
      const name = station ? station.name : serial;
      const wb = await buildCloseReportTemplateWorkbook(merged, name, accessToken);
      const safeName = name.replace(/[^A-Za-z0-9_-]+/g, '_');
      XLSX.writeFile(wb, `custom_close_report_${safeName}_${fromDate}_to_${toDate}.xlsx`);

      const parts = [tr("Downloaded: {0} to {1}, from {2} daily close report(s).",
        minuteOf(merged.start_date), minuteOf(merged.end_date), reports.length)];
      const gaps = findGaps(reports);
      if (gaps.length) {
        parts.push(tr("Warning: missing close report(s) for {0} — sales and deliveries in those hours are not included.",
          gaps.map((g) => `${g.from} → ${g.to}`).join(', ')));
      }
      setMessage(parts.join(' '));
    } catch (err) {
      setMessage(tr("Error: {0}", err.message || err));
    } finally {
      setBusy(false);
      setProgress('');
    }
  };

  return (
    <div className="FilterBar">
      <p><b>{tr("Custom Close Report ")}</b></p>

      {stations.length > 1 ? (
        <FormControl className={classes.formControl} style={{ minWidth: 320 }}>
          <Autocomplete
            options={stations}
            getOptionLabel={(option) => option.name || ''}
            value={station || null}
            onChange={(event, value) => setSerial(value ? value.serial_number : '')}
            renderInput={(params) => <TextField {...params} placeholder={tr("Search stations...")} />}
          />
          <FormHelperText>{tr("Station")}</FormHelperText>
        </FormControl>
      ) : (station && (
        <FormControl className={classes.formControl}>
          <TextField label={tr("Station")} value={station.name} InputProps={{ readOnly: true }} />
        </FormControl>
      ))}

      <FormControl className={classes.formControl} noValidate>
        <TextField
          label={tr("Start Date")}
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ max: toDate || undefined }}
        />
        <FormHelperText>{tr("First close report starts this day")}</FormHelperText>
      </FormControl>

      <FormControl className={classes.formControl} noValidate>
        <TextField
          label={tr("End Date")}
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ min: fromDate || undefined }}
        />
        <FormHelperText>{tr("Last close report starts this day")}</FormHelperText>
      </FormControl>

      <div style={{ marginTop: 10 }}>
        <Button variant="primary" disabled={busy} onClick={handleGenerate}>
          {busy ? tr("Generating…") : tr("Download Custom Close Report (Excel)")}
        </Button>
        {busy && progress && (
          <span style={{ marginLeft: 10, fontStyle: 'italic' }}>{progress}</span>
        )}
      </div>

      {message && <p style={{ marginTop: 10 }}><b>{message}</b></p>}
    </div>
  );
}